import { useTranslation } from 'react-i18next';
import { LayersControl, GeoJSON } from 'react-leaflet';
import { Feature, FeatureCollection, Geometry, Point } from 'geojson';
import { PropsWithChildren, ReactElement, useCallback } from 'react';
import * as L from 'leaflet';
import { useGeofenceTooltipEventHandlers } from './useGeofenceTooltipEventHandlers';
import { GeofenceProperties } from '@customer/store/apis/interactionsApi';

const StrokeRegularWeight = 2;
const StrokeRegularOpacity = 1;
export const FillRegularOpacity = 0.2;
export const FillHoverOpacity = 0.4;
export const GeofenceRegularColor = '#3388FF';

export const commonPathDisplay: L.PathOptions = {
  color: GeofenceRegularColor,
  weight: StrokeRegularWeight,
  opacity: StrokeRegularOpacity,
  fillColor: GeofenceRegularColor,
  fillOpacity: FillRegularOpacity,
};

export interface GeofenceLayerGroupProps {
  layerNameTrans?: string;
  checked?: boolean;
  geoJson?: FeatureCollection<Geometry, GeofenceProperties>;
  filter?: (feature: Feature<Geometry, GeofenceProperties>) => boolean;
  pathOptions?: L.PathOptions;
}

export function GeofenceLayerGroup({
  layerNameTrans,
  checked = true,
  geoJson,
  filter,
  pathOptions = commonPathDisplay,
  children, // Allow passing interactive children to the GeoJSON layer (Tooltip, Popup)
}: PropsWithChildren<GeofenceLayerGroupProps>): ReactElement | null {
  const { t } = useTranslation();

  const eventHandlers = useGeofenceTooltipEventHandlers();

  const pointToCircle = useCallback(
    (feature: Feature<Point, GeofenceProperties>, latlng: L.LatLng): L.Layer => {
      return L.circle(latlng, {
        ...pathOptions,
        radius: feature.properties.radius,
      });
    },
    [pathOptions],
  );

  if (geoJson === undefined) {
    return null;
  }

  const controlName = layerNameTrans ?? 'Model.Plural.Geofence_other';

  return (
    <LayersControl.Overlay
      key={controlName} // The key must change for control name changes to be rendered.
      name={t(controlName)}
      checked={checked}
    >
      <GeoJSON
        data={geoJson}
        eventHandlers={eventHandlers}
        style={pathOptions}
        filter={filter}
        pointToLayer={pointToCircle}
      >
        {children}
      </GeoJSON>
    </LayersControl.Overlay>
  );
}

export default GeofenceLayerGroup;
